import { Form, Input } from "antd";
import { NamePath } from "antd/lib/form/interface";
import type {
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  SyntheticEvent,
} from "react";
import { useRef } from "react";
import { Styles } from "./styled";

interface IProps {
  name: NamePath;
  placeholder?: string;
  editable?: boolean;
  readonlyText?: string;
}

const { TextArea } = Input;

const trimMemoValue = (value: string): string => value.trim();

const fixAutoPeriodAfterSpace = (prev: string, next: string): string | null => {
  if (next === `${prev}.`) return `${prev} `;
  if (next === `${prev}. `) return `${prev}  `;
  return null;
};

const restoreSelection = (el: HTMLTextAreaElement, cursor: number) => {
  requestAnimationFrame(() => {
    el.setSelectionRange(cursor, cursor);
  });
};

const TextField = (props: IProps) => {
  const {
    name,
    placeholder = "メモを入力してください",
    editable = true,
    readonlyText = "",
  } = props;
  const form = Form.useFormInstance();
  const lastKeyRef = useRef("");
  const valueBeforeKeyRef = useRef("");

  if (!editable) {
    const display = readonlyText.trim() ? readonlyText : "--";
    return (
      <Styles className="timeline-text timeline-text-readonly">
        <span className="readonly-line">{display}</span>
      </Styles>
    );
  }

  const setMemoValue = (
    el: HTMLTextAreaElement,
    value: string,
    cursor: number,
  ) => {
    form.setFieldValue(name, value);
    restoreSelection(el, cursor);
  };

  const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    const raw = event.target.value;
    const trimmed = trimMemoValue(raw);
    if (trimmed !== raw) {
      form.setFieldValue(name, trimmed);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    lastKeyRef.current = event.key;
    valueBeforeKeyRef.current = event.currentTarget.value;

    if (event.key !== " " && event.code !== "Space") return;

    const el = event.currentTarget;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const charBefore = start > 0 ? el.value[start - 1] : "";

    if (charBefore !== " ") return;

    event.preventDefault();
    const next = el.value.slice(0, start) + " " + el.value.slice(end);
    setMemoValue(el, next, start + 1);
  };

  const handleBeforeInput = (
    event: SyntheticEvent<HTMLTextAreaElement, InputEvent>,
  ) => {
    if (lastKeyRef.current !== " ") return;
    const data = event.nativeEvent.data;
    if (data !== "." && data !== ". ") return;
    event.preventDefault();
    const el = event.currentTarget;
    const prev = valueBeforeKeyRef.current;
    const start = el.selectionStart ?? prev.length;
    const replacement = data === ". " ? "  " : " ";
    const next = prev.slice(0, start) + replacement + prev.slice(start);
    setMemoValue(el, next, start + replacement.length);
  };

  const handleInput = (event: FormEvent<HTMLTextAreaElement>) => {
    if (lastKeyRef.current !== " ") return;

    const el = event.currentTarget;
    const prev = valueBeforeKeyRef.current;
    const next = el.value;
    const fixed = fixAutoPeriodAfterSpace(prev, next);
    if (fixed == null || fixed === next) return;

    setMemoValue(el, fixed, fixed.length);
  };

  return (
    <Styles className="timeline-text">
      <Form.Item name={name}>
        <TextArea
          placeholder={placeholder}
          autoSize
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onBeforeInput={handleBeforeInput}
          onInput={handleInput}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          spellCheck={false}
          lang="ja"
        />
      </Form.Item>
    </Styles>
  );
};

export default TextField;
